import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

// ─── OilFlow ──────────────────────────────────────────────────────────────────
//
// Renders a single 3D-looking oil drop on #oil-canvas (position: fixed).
//
// Shape: rotationally-symmetric teardrop SDF in the fragment shader.
//   - 3D sphere normals for the body (proper Fresnel, specular, depth)
//   - 2D SDF gradient for the narrow tail
//
// Phases:
//   hidden → spawning  (#pour ScrollTrigger drives position + shape)
//   spawning → falling  (drop detaches, gravity physics)
//   falling → pooling   (drop reaches footer, spreads flat)

export default class OilFlow {

    constructor() {
        this.canvas = document.getElementById('oil-canvas')

        this.W = 0; this.H = 0; this.dpr = 1

        this.scrollY        = window.scrollY

        // Scroll-driven fall path (set on detach from #pour)
        this.footerLogoDocY  = 0   // document-space Y of rest position (above footer logo)
        this._scrollAtDetach = 0   // scrollY when drop leaves #pour
        this._startDocY      = 0   // drop document-Y at detach
        this._scrollRange    = 1   // total scroll distance remaining after detach
        this._radiusAtDetach = 62  // radius snapshot when drop leaves #pour

        this.drop = {
            x:          window.innerWidth  * 0.5,
            y:          -300,
            radius:     0,
            elongation: 0,    // 0 = sphere, 1 = full teardrop
            opacity:    0,
            spread:     0, // Додайте це сюди
            time: 0, // <--- ДОДАНО
        }

        this.phase    = 'hidden'
        this.lastTime = 0

        if (!this._initGL()) return
        this._buildShader()
        this._buildQuad()
        this._resize()
        this._buildRects()

        window.addEventListener('resize',
            () => { this._resize(); this._buildRects() },
            { passive: true })
        window.addEventListener('scroll',
            () => { this.scrollY = window.scrollY },
            { passive: true })

        this._setupScrollTrigger()
        this._startLoop()
    }

    // ── WebGL2 ────────────────────────────────────────────────────────────────

    _initGL() {
        const gl = this.canvas.getContext('webgl2',
            { alpha: true, premultipliedAlpha: true })
        if (!gl) { console.warn('OilFlow: WebGL2 not available'); return false }
        this.gl = gl
        gl.enable(gl.BLEND)
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
        gl.clearColor(0, 0, 0, 0)
        return true
    }

    // ── Shader ────────────────────────────────────────────────────────────────

    _buildShader() {
        const gl = this.gl

        const vert = /* glsl */`#version 300 es
        in vec2 aPos;
        void main() { gl_Position = vec4(aPos, 0.0, 1.0); }`

        const frag = /* glsl */`#version 300 es
        precision highp float;

        // All lengths in physical canvas pixels (CSS px × dpr)
        uniform vec2  uResolution;
        uniform vec2  uCenter;       // drop centre, top-left origin
        uniform float uRadius;
        uniform float uElongation;   // 0 = sphere  →  1 = full teardrop
        uniform float uSpread;       // 0 = drop    →  1 = flat footer pool
        uniform float uOpacity;

        uniform float uTime; // Додаємо нову uniform для анімації краплі

        out vec4 outColor;

        // ── SDF helpers ────────────────────────────────────────────────────────

        float smin(float a, float b, float k) {
            float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
            return mix(b, a, h) - k * h * (1.0 - h);
        }

        // ── Base teardrop SDF (pure geometry, no wave animation) ───────────────
        //   Used for 3D normal computation via revolution operator.
        //   Coordinate convention: top-left origin, y increases downward.
        //   Body  (rounded) → BELOW centre  (+y)
        //   Tail  (pointed) → ABOVE centre  (−y)
        //
        float sdDropBase(vec2 q) {
            float r = uRadius;

            if (uSpread > 0.01) {
                float sx = 1.0 + uSpread * 5.8;
                float sy = max(0.07, 1.0 - uSpread * 0.93);
                return length(q * vec2(1.0 / sx, 1.0 / sy)) - r;
            }

            float bodyOffY = r * uElongation * 0.30;
            float sphere   = length(q - vec2(0.0, bodyOffY)) - r;
            if (uElongation < 0.02) return sphere;

            float blendY = bodyOffY - r * 0.52;
            float tipY   = blendY  - r * uElongation * 2.05;
            float segLen = blendY  - tipY;

            float t    = clamp((q.y - tipY) / segLen, 0.0, 1.0);
            float capR = r * 0.38 * t;
            float cy   = tipY + t * segLen;
            float tail = length(vec2(q.x, q.y - cy)) - capR;

            return smin(sphere, tail, r * 0.46);
        }

        // ── Animated teardrop SDF (base + surface wobble) ─────────────────────
        //   Used for the shape mask and depth shading in main().
        //
        float sdDrop(vec2 q) {
            if (uRadius > 0.0) {
                float wave = sin(q.y * 0.03 + uTime * 2.5) * cos(q.x * 0.03 + uTime * 1.2);
                q.x += wave * 2.0 * uElongation;
            }
            return sdDropBase(q);
        }

        // ── 3D SDF via revolution operator (Inigo Quilez) ─────────────────────
        //   Revolves the 2D base profile around the Y axis.
        //   sdf3D(x,y,z) = sdDropBase( sqrt(x²+z²), y )
        //
        float sdf3D(vec3 p) {
            return sdDropBase(vec2(length(p.xz), p.y));
        }

        // ── 3D normal — gradient of the true revolution surface ────────────────
        //   No seams: the normal is the analytic gradient of the single unified
        //   3D SDF, continuous everywhere (including the sphere↔cone junction).
        //
        //   Algorithm:
        //     1. Find local profile radius R(y) = -sdDropBase(0, y)  (axis depth)
        //     2. Front-surface z = sqrt( R² - qx² )
        //     3. Central-difference gradient of sdf3D at (qx, qy, zFront)
        //
        vec3 dropNormal3D(vec2 q) {
            // Profile radius at this y (distance from axis to surface boundary)
            float localR = max(0.0, -sdDropBase(vec2(0.0, q.y)));

            // Front-face z on the surface of revolution
            float zFront = sqrt(max(0.0, localR * localR - q.x * q.x));

            // True 3D surface normal via central differences on sdf3D
            vec3 p   = vec3(q.x, q.y, zFront);
            float e  = 1.6;
            return normalize(vec3(
                sdf3D(p + vec3(e, 0, 0)) - sdf3D(p - vec3(e, 0, 0)),
                sdf3D(p + vec3(0, e, 0)) - sdf3D(p - vec3(0, e, 0)),
                sdf3D(p + vec3(0, 0, e)) - sdf3D(p - vec3(0, 0, e))
            ));
        }

        void main() {
            vec2 p = vec2(gl_FragCoord.x, uResolution.y - gl_FragCoord.y);
            vec2 q = p - uCenter;

            float r    = uRadius;
            float halo = r * 0.20;

            float d = sdDrop(q);
            if (d > halo) { outColor = vec4(0.0); return; }

            // ── 3D surface normal ──────────────────────────────────────────────
            vec3 n3 = dropNormal3D(q);

            // Camera is looking along +z (orthographic)
            vec3 viewDir = vec3(0.0, 0.0, 1.0);
            float NdotV  = n3.z;   // dot(n3, viewDir)

            // ── Fresnel (Schlick) ──────────────────────────────────────────────
            // Oil IOR ≈ 1.47 → F0 ≈ 0.037
            float F0      = 0.037;
            float fresnel = F0 + (1.0 - F0) * pow(1.0 - NdotV, 5.0);
            // Boost for visible effect
            fresnel       = pow(1.0 - NdotV, 3.2) * 0.75 + fresnel * 0.25;

            // ── Vertical colour gradient ───────────────────────────────────────
            float yFac   = clamp((q.y + r * 2.1) / (r * 4.0), 0.0, 1.0);
vec3 colHi   = vec3(0.96, 0.88, 0.62);
vec3 colMid  = vec3(0.86, 0.68, 0.30);
vec3 colDeep = vec3(0.62, 0.38, 0.14);
            vec3 base    = mix(mix(colHi, colMid, yFac), colDeep, yFac * 0.55);

            // ── Lights (3D, Blinn-Phong) ───────────────────────────────────────
            // Key light — upper-right, slightly in front
            vec3 kLight  = normalize(vec3( 0.55, -0.76,  0.90));
            vec3 kHalf   = normalize(kLight + viewDir);
            float kSpec  = pow(max(dot(n3, kHalf), 0.0), 72.0) * 0.92;

            // Secondary highlight — left rim (catches subsurface)
            vec3 sLight  = normalize(vec3(-0.60, -0.30,  0.65));
            vec3 sHalf   = normalize(sLight + viewDir);
            float sSpec  = pow(max(dot(n3, sHalf), 0.0), 18.0) * 0.28;

            // Fill — soft, lower-left
            vec3 fLight  = normalize(vec3(-0.38,  0.45,  0.62));
            float fDiff  = max(dot(n3, fLight), 0.0);

            // Rim glow — back-scatter approximation
            float rim    = pow(max(1.0 - NdotV, 0.0), 4.5);

            // ── Surface shading ────────────────────────────────────────────────
            float alpha;
            vec3  color;

            if (d < 0.0) {
                // Interior of drop
                // Isotropic color model: no n3/fresnel/spec terms in interior.
                // This prevents any geometric rhombus/facet artifacts.
                vec2 qn      = q / vec2(r * 0.92, r * 1.12);
                float radial = clamp(length(qn), 0.0, 1.0);
                float gy     = smoothstep(-r * 1.35, r * 0.95, q.y);
                float edge   = smoothstep(0.76, 1.0, radial);

                // Amber body + stronger right-side yellow volume.
                color  = mix(colHi, colMid, gy);
                color  = mix(color, colDeep, smoothstep(0.64, 1.0, radial) * 0.20);
                color += vec3(0.94, 0.62, 0.10) * edge * 0.34;
                float rightBright = smoothstep(0.10 * r, 0.62 * r, q.x) * (1.0 - smoothstep(0.94 * r, 1.06 * r, q.x));
                rightBright *= smoothstep(-1.00 * r, 0.82 * r, q.y);
                color = mix(color, vec3(0.99, 0.90, 0.14), rightBright * 0.82);

                // Asymmetric darker amber mass on the left side (reference-like).
                float leftMask = smoothstep(-0.98 * r, -0.28 * r, q.x) * (1.0 - smoothstep(-0.28 * r, -0.12 * r, q.x));
                float bodyMask = smoothstep(-0.62 * r, 0.56 * r, q.y) * (1.0 - smoothstep(0.56 * r, 0.99 * r, q.y));
                float leftCore = leftMask * bodyMask * (1.0 - smoothstep(0.74, 0.98, radial));
                color = mix(color, vec3(0.58, 0.23, 0.02), leftCore * 0.48);

                // Darker contour near left rim for depth.
                float leftRim = smoothstep(-0.90 * r, -0.60 * r, q.x) * (1.0 - smoothstep(-0.60 * r, -0.40 * r, q.x));
                leftRim *= smoothstep(-0.90 * r, 0.52 * r, q.y) * (1.0 - smoothstep(0.52 * r, 0.98 * r, q.y));
                color += vec3(0.58, 0.16, 0.01) * leftRim * 0.18;

                // Broad left amber core (replace thin red stripe with soft volume).
                vec2 qLeftBlob = vec2((q.x + 0.34 * r) / (0.26 * r), (q.y - 0.02 * r) / (0.46 * r));
                float leftBlob = exp(-dot(qLeftBlob, qLeftBlob));
                color = mix(color, vec3(0.77, 0.40, 0.06), leftBlob * 0.30);

                // Large white crescent highlight on the left.
                vec2 qCres = vec2((q.x + 0.55 * r) / (0.30 * r), (q.y + 0.02 * r) / (0.72 * r));
                float cresOuter = exp(-dot(qCres, qCres));
                vec2 qCresIn = vec2((q.x + 0.42 * r) / (0.21 * r), (q.y + 0.04 * r) / (0.58 * r));
                float cresInner = exp(-dot(qCresIn, qCresIn));
                float crescent = max(0.0, cresOuter - cresInner * 0.92);
                float cresMask = smoothstep(-0.92 * r, 0.40 * r, q.y) * (1.0 - smoothstep(0.40 * r, 0.88 * r, q.y));
                color += vec3(1.0, 0.99, 0.96) * crescent * cresMask * 1.22;

                // Bottom internal glow and hot amber base.
                vec2 qb = (q - vec2(0.0, 0.60 * r)) / vec2(0.74 * r, 0.50 * r);
                float bottomGlow = exp(-dot(qb, qb));
                color += vec3(0.98, 0.86, 0.40) * bottomGlow * 0.26;
                vec2 qBaseHot = vec2((q.x + 0.28 * r) / (0.34 * r), (q.y - 0.88 * r) / (0.10 * r));
                float baseHot = exp(-dot(qBaseHot, qBaseHot));
                float baseHotMask = smoothstep(0.42 * r, 0.98 * r, q.y);
                color += vec3(0.90, 0.20, 0.02) * baseHot * baseHotMask * 0.22;

                // Strongly curved side reflections (dominant left, weaker right).
                float ny = clamp(q.y / r, -1.0, 1.0);
                float bow = pow(1.0 - ny * ny, 1.35); // stronger body curvature
                float taperTop = smoothstep(-1.02, -0.30, ny);
                float xL = -0.15 * r - 0.34 * r * bow + 0.05 * r * taperTop;
                float xR =  0.08 * r + 0.24 * r * bow - 0.03 * r * taperTop;
                float wL = (0.050 + 0.040 * bow) * r;
                float wR = (0.048 + 0.032 * bow) * r;
                float hL = 0.70 * r;
                float hR = 0.62 * r;
                vec2 qL = vec2((q.x - xL) / wL, (q.y + 0.20 * r) / hL);
                vec2 qR = vec2((q.x - xR) / wR, (q.y + 0.18 * r) / hR);
                float streakMask = (1.0 - smoothstep(0.89, 1.0, radial)) * smoothstep(-1.00 * r, 0.46 * r, q.y);
                float streakL = exp(-dot(qL, qL));
                float streakR = exp(-dot(qR, qR));
                color += vec3(1.0, 0.99, 0.95) * (streakL * 0.24 + streakR * 0.17) * streakMask;

                // Bottom curved reflection arc to enhance round volume.
                vec2 qArc = vec2(q.x / (0.62 * r), (q.y - 0.68 * r) / (0.20 * r));
                float arc = exp(-dot(qArc, qArc)) * smoothstep(0.18, 0.95, radial);
                color += vec3(1.0, 0.90, 0.62) * arc * 0.26;

                // Thin top glint along the needle axis.
                vec2 qt = (q - vec2(0.0, -0.84 * r)) / vec2(0.06 * r, 0.22 * r);
                float topGlint = exp(-dot(qt, qt));
                float topGlintMask = 1.0 - smoothstep(-0.22 * r, 0.12 * r, q.y);
                color += vec3(1.0, 0.99, 0.93) * topGlint * topGlintMask * 0.34;

                // Warm vertical lane on the right half.
                vec2 qLane = vec2((q.x - 0.34 * r) / (0.16 * r), (q.y + 0.02 * r) / (0.78 * r));
                float lane = exp(-dot(qLane, qLane));
                color += vec3(1.0, 0.92, 0.20) * lane * 0.18;

                // Soft center lift to avoid a flame-like dark vertical spike.
                vec2 qCenter = vec2((q.x - 0.02 * r) / (0.36 * r), (q.y + 0.00 * r) / (0.56 * r));
                float centerLift = exp(-dot(qCenter, qCenter));
                color += vec3(0.99, 0.86, 0.40) * centerLift * 0.30;
                vec2 qCenterClean = vec2((q.x + 0.05 * r) / (0.34 * r), (q.y + 0.04 * r) / (0.50 * r));
                float centerClean = exp(-dot(qCenterClean, qCenterClean));
                color = mix(color, vec3(0.98, 0.84, 0.38), centerClean * 0.30);

                // Additional vertical cleanup to remove residual center stripe.
                vec2 qStripeClean = vec2((q.x + 0.08 * r) / (0.18 * r), (q.y + 0.05 * r) / (0.50 * r));
                float stripeClean = exp(-dot(qStripeClean, qStripeClean));
                color = mix(color, vec3(0.98, 0.85, 0.40), stripeClean * 0.06);

                // Small warm sparkle.
                vec2 qs = (q - vec2(0.06 * r, 0.02 * r)) / vec2(0.17 * r, 0.15 * r);
                float spark = exp(-dot(qs, qs));
                color += vec3(1.0, 0.97, 0.84) * spark * 0.34;

                alpha = 0.64;

            } else {
                // Halo band
                float t  = 1.0 - d / halo;
                alpha    = t * t * 0.18;
                color    = mix(colMid, vec3(1.0, 0.88, 0.40), t * 0.25);
            }

            alpha *= uOpacity;
            outColor = vec4(color * alpha, alpha);   // premultiplied
        }`

        const vs   = this._compile(gl.VERTEX_SHADER,   vert)
        const fs   = this._compile(gl.FRAGMENT_SHADER, frag)
        const prog = gl.createProgram()
        gl.attachShader(prog, vs); gl.attachShader(prog, fs)
        gl.linkProgram(prog)
        if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
            console.error('OilFlow link error:', gl.getProgramInfoLog(prog))
        }
        gl.deleteShader(vs); gl.deleteShader(fs)
        this.program = prog

        this.uLoc = {
            resolution:  gl.getUniformLocation(prog, 'uResolution'),
            center:      gl.getUniformLocation(prog, 'uCenter'),
            radius:      gl.getUniformLocation(prog, 'uRadius'),
            elongation:  gl.getUniformLocation(prog, 'uElongation'),
            spread:      gl.getUniformLocation(prog, 'uSpread'),
            opacity:     gl.getUniformLocation(prog, 'uOpacity'),
            time: gl.getUniformLocation(prog, 'uTime'),
        }
    }

    _compile(type, src) {
        const gl = this.gl
        const s  = gl.createShader(type)
        gl.shaderSource(s, src)
        gl.compileShader(s)
        if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
            console.error('OilFlow shader error:', gl.getShaderInfoLog(s))
        }
        return s
    }

    // ── Full-screen quad ──────────────────────────────────────────────────────

    _buildQuad() {
        const gl = this.gl
        this.vao = gl.createVertexArray()
        gl.bindVertexArray(this.vao)
        const vbo = gl.createBuffer()
        gl.bindBuffer(gl.ARRAY_BUFFER, vbo)
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
            -1,-1,  1,-1, -1, 1,
            -1, 1,  1,-1,  1, 1,
        ]), gl.STATIC_DRAW)
        const loc = gl.getAttribLocation(this.program, 'aPos')
        gl.enableVertexAttribArray(loc)
        gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0)
        gl.bindVertexArray(null)
    }

    // ── Resize ────────────────────────────────────────────────────────────────

    _resize() {
        this.dpr = Math.min(window.devicePixelRatio || 1, 2)
        this.W   = Math.round(window.innerWidth  * this.dpr)
        this.H   = Math.round(window.innerHeight * this.dpr)
        this.canvas.width  = this.W
        this.canvas.height = this.H
        if (this.gl) this.gl.viewport(0, 0, this.W, this.H)
    }

    // ── Footer logo document-space Y (cached on init/resize) ─────────────────
    // The drop's rest position is above the first block of content in #footer.

    _buildRects() {
        // Використовуємо setTimeout, щоб дати GSAP час розрахувати всі піни та висоти
        setTimeout(() => {
            const footerLogo = document.querySelector('.footer__logo') || document.getElementById('footer');
            if (footerLogo) {
                // Використовуємо offsetTop для ігнорування поточних трансформацій
                let offset = 0;
                let el = footerLogo;
                while (el) {
                    offset += el.offsetTop;
                    el = el.offsetParent;
                }
                this.footerLogoDocY = offset; // Точка над логотипом
            } else {
                this.footerLogoDocY = document.documentElement.scrollHeight - 10;
            }
        }, 100);
    }

    // ── ScrollTrigger ─────────────────────────────────────────────────────────

    _setupScrollTrigger() {
        if (!document.querySelector('#pour')) return
        const self = this

        ScrollTrigger.create({
            trigger: '#pour',
            start:   'top top',
            end:     'bottom bottom',
            scrub:   true,
            onUpdate(st) {
                const p = st.progress
                
                // Якщо ми в зоні першої секції, фаза завжди має бути spawning
                // це дозволить кулі зникати/з'являтися при скролі в обидва боки
                self.phase = 'spawning'

                // 1. Повне зникнення в самому верху
                if (p < 0.05) {
                    self.drop.opacity = 0
                    self.drop.radius = 0
                    self.drop.elongation = 0
                    return
                }

                // 2. Розрахунок прозорості та радіуса
                self.drop.opacity = Math.min((p - 0.05) / 0.1, 1.0)
                
                const growT = Math.min((p - 0.05) / 0.3, 1.0)
                self.drop.radius = 62 * easeOut(growT)

                // 3. Елонгація (перетворення з кулі в краплю)
                // Коли скролимо вгору (p зменшується), elongT стане 0 і крапля стане кулею
                const elongT = Math.max(0, Math.min((p - 0.3) / 0.5, 1.0))
                self.drop.elongation = 0.88 * easeInOut(elongT)
                
                self.drop.x = window.innerWidth * 0.5
                self.drop.y = window.innerHeight * 0.5
            },
            onLeave() {
                self.phase = 'falling'
                self._scrollAtDetach = window.scrollY
                self._radiusAtDetach = self.drop.radius
                self._startDocY = (window.innerHeight * 0.5) + window.scrollY

                const maxScroll = document.documentElement.scrollHeight - window.innerHeight
                self._scrollRange = Math.max(1, maxScroll - self._scrollAtDetach)
            },
            onEnterBack() {
                self.phase = 'spawning'
            }
        })
    }

    // ── Scroll-driven position update ─────────────────────────────────────────
    // No physics — the drop position is 100 % determined by scrollY.
    // Scrolling down moves the drop toward the footer logo;
    // scrolling back up reverses the path exactly.

    _update(_dt) {
        this.drop.time += _dt; 

        // Перевірка: якщо ми не в фазі падіння або скрол повернувся вгору — виходимо
        if (this.phase !== 'falling' || window.scrollY < this._scrollAtDetach) {
            return;
        }

        const d = this.drop;
        const currentScroll = window.scrollY;
        
        let progress = (currentScroll - this._scrollAtDetach) / this._scrollRange;
        progress = Math.max(0, Math.min(1, progress));

         // Рух по Y: від центру viewport (50%) до кінцевої позиції (86%)
        const fromY = window.innerHeight * 0.50;
        const toY   = window.innerHeight * 0.88;
        d.x = window.innerWidth  * 0.5;
        d.y = fromY + (toY - fromY) * easeInOut(Math.min(progress / 0.8, 1.0));

        // Зменшення на 50% в останні 30% скролу
        const shrinkT  = Math.max(0, (progress - 0.70) / 0.30);
        d.radius       = this._radiusAtDetach * (1.0 - 0.5 * easeInOut(shrinkT));

        d.opacity      = 1.0;
        d.elongation   = 0.88;
    }

    // ── Render loop ───────────────────────────────────────────────────────────

    _startLoop() {
        const loop = (ts) => {
            if (!this.lastTime) this.lastTime = ts
            const dt = Math.min((ts - this.lastTime) * 0.001, 0.05)
            this.lastTime = ts
            this._update(dt)
            this._draw()
            requestAnimationFrame(loop)
        }
        requestAnimationFrame(loop)
    }

    _draw() {
        const gl = this.gl
        gl.clear(gl.COLOR_BUFFER_BIT)

        const d = this.drop
        if (d.opacity < 0.005 || d.radius < 0.5) return

        gl.useProgram(this.program)
        gl.uniform2f(this.uLoc.resolution, this.W, this.H)
        gl.uniform2f(this.uLoc.center,     d.x         * this.dpr, d.y         * this.dpr)
        gl.uniform1f(this.uLoc.radius,     d.radius    * this.dpr)
        gl.uniform1f(this.uLoc.elongation, d.elongation)
        gl.uniform1f(this.uLoc.spread,     d.spread)
        gl.uniform1f(this.uLoc.opacity,    d.opacity)
        gl.uniform1f(this.uLoc.time, this.drop.time);
        gl.bindVertexArray(this.vao)
        gl.drawArrays(gl.TRIANGLES, 0, 6)
        gl.bindVertexArray(null)
    }
}



// ── Easing ────────────────────────────────────────────────────────────────────

function easeOut(t)   { return 1 - (1 - t) * (1 - t) }
function easeInOut(t) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2 }